import { getAuthToken } from '../../services/auth/tokenStorage';
import React, { useEffect, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { BottomNavBar } from '../../components/BottomNavBar';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../../config';
import { ChecklistCardScroll } from '../../components/ChecklistCardScroll';
import { RootStackParamList } from '../../types/navigation';
import { TaskItem } from '../../components/TaskItem';
import { CONTACTS_TASKS_CONFIG, PERSONAL_TASKS_CONFIG, MEDICAL_TASKS_CONFIG, PASSWORDS_TASKS_CONFIG, EXPENSES_TASKS_CONFIG, FINANCE_TASKS_CONFIG, PROPERTY_TASKS_CONFIG, FINAL_WISHES_TASKS_CONFIG, HEALTHCARE_DIRECTIVES_TASKS_CONFIG, ESTATE_PLANNING_TASKS_CONFIG, CARE_PREFERENCES_TASKS_CONFIG, LOYALTY_POINTS_TASKS_CONFIG } from '../../constants/taskIds';
import { contactService } from '../../services/contactService';
import { vaultService } from '../../services/vaultService';
import { loyaltyPointService } from '../../services/loyaltyPointService';
import { expenseService } from '../../services/expenseService';
import { documentStorageService } from '../../services/documentStorageService';
import { useUserSelection } from '../../contexts/UserSelectionContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { UpgradeBanner } from '../../components/UpgradeBanner';

import { createLogger } from '../../utils/logger';
const logger = createLogger('EmergencyContactsChecklist');

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

interface Task {
  id: string;
  title: string;
}

interface Category {
  id: string;
  name: string;
  icon: any;
  count: number;
  totalTasks: number;
}

// Use the new contacts tasks configuration
const EMERGENCY_CONTACTS_TASKS = CONTACTS_TASKS_CONFIG;

const categories: Category[] = [
  { id: 'all', name: 'All Tasks', icon: require('../../assets/icons/all-tasks.png'), count: 0, totalTasks: PERSONAL_TASKS_CONFIG.length + MEDICAL_TASKS_CONFIG.length + FINANCE_TASKS_CONFIG.length + PROPERTY_TASKS_CONFIG.length + FINAL_WISHES_TASKS_CONFIG.length + HEALTHCARE_DIRECTIVES_TASKS_CONFIG.length + ESTATE_PLANNING_TASKS_CONFIG.length + CARE_PREFERENCES_TASKS_CONFIG.length + PASSWORDS_TASKS_CONFIG.length + CONTACTS_TASKS_CONFIG.length + EXPENSES_TASKS_CONFIG.length + LOYALTY_POINTS_TASKS_CONFIG.length + 8 },
  { id: 'personal', name: 'Personal', icon: require('../../assets/icons/personal.png'), count: 0, totalTasks: PERSONAL_TASKS_CONFIG.length },
  { id: 'medical', name: 'Medical', icon: require('../../assets/icons/medical.png'), count: 0, totalTasks: MEDICAL_TASKS_CONFIG.length },
  { id: 'finance', name: 'Finance', icon: require('../../assets/icons/financial.png'), count: 0, totalTasks: FINANCE_TASKS_CONFIG.length },
  { id: 'property', name: 'Property', icon: require('../../assets/icons/property.png'), count: 0, totalTasks: PROPERTY_TASKS_CONFIG.length },
  { id: 'finalWishes', name: 'Final Wishes', icon: require('../../assets/icons/final-wishes.png'), count: 0, totalTasks: FINAL_WISHES_TASKS_CONFIG.length },
  { id: 'healthcareDirectives', name: 'Healthcare Directives', icon: require('../../assets/icons/healthcare.png'), count: 0, totalTasks: HEALTHCARE_DIRECTIVES_TASKS_CONFIG.length },
  { id: 'estatePlanning', name: 'Estate Planning', icon: require('../../assets/icons/estate-planning.png'), count: 0, totalTasks: ESTATE_PLANNING_TASKS_CONFIG.length },
  { id: 'carePreferences', name: 'Care Preferences', icon: require('../../assets/icons/final-care.png'), count: 0, totalTasks: CARE_PREFERENCES_TASKS_CONFIG.length },
  { id: 'passwords', name: 'Passwords', icon: require('../../assets/icons/password.png'), count: 0, totalTasks: PASSWORDS_TASKS_CONFIG.length },
  { id: 'contacts', name: 'Contacts', icon: require('../../assets/icons/all-contacts.png'), count: 0, totalTasks: CONTACTS_TASKS_CONFIG.length },
  { id: 'expenses', name: 'Expenses', icon: require('../../assets/icons/expenses.png'), count: 0, totalTasks: EXPENSES_TASKS_CONFIG.length },
  { id: 'documents', name: 'Documents', icon: require('../../assets/icons/folder.png'), count: 0, totalTasks: 8 },
  { id: 'loyaltyPoints', name: 'Loyalty Points', icon: require('../../assets/icons/loyalty.png'), count: 0, totalTasks: LOYALTY_POINTS_TASKS_CONFIG.length },
];

export const EmergencyContactsChecklistScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const { isViewingOthersData, selectedUserId, availablePrimaryUsers } = useUserSelection();
  const { canViewOwnData } = useSubscription();
  const [selectedCategory, setSelectedCategory] = useState('contacts');
  const [completedTaskIds, setCompletedTaskIds] = useState<string[]>([]);
  const [categoryProgress, setCategoryProgress] = useState<Record<string, number>>({});
  const [completedContactTasks, setCompletedContactTasks] = useState<string[]>([]);
  const [completedPasswordTasks, setCompletedPasswordTasks] = useState<string[]>([]);
  const [completedPersonalTasks, setCompletedPersonalTasks] = useState<string[]>([]);
  const [completedMedicalTasks, setCompletedMedicalTasks] = useState<string[]>([]);
  const [completedFinanceTasks, setCompletedFinanceTasks] = useState<string[]>([]);
  const [completedPropertyTasks, setCompletedPropertyTasks] = useState<string[]>([]);
  const [completedFinalWishesTasks, setCompletedFinalWishesTasks] = useState<string[]>([]);
  const [completedHealthcareDirectivesTasks, setCompletedHealthcareDirectivesTasks] = useState<string[]>([]);
  const [completedEstatePlanningTasks, setCompletedEstatePlanningTasks] = useState<string[]>([]);
  const [completedCarePreferencesTasks, setCompletedCarePreferencesTasks] = useState<string[]>([]);
  const [completedExpenseTasks, setCompletedExpenseTasks] = useState<string[]>([]);
  const [completedLoyaltyPointTasks, setCompletedLoyaltyPointTasks] = useState<string[]>([]);
  const [completedDocumentTasks, setCompletedDocumentTasks] = useState<string[]>([]);

  // Get the name of the user being viewed
  const viewingUserName = isViewingOthersData
    ? availablePrimaryUsers.find(u => u.id === selectedUserId)?.name || 'User'
    : null;

  // Extract fetchContactCompletion to useCallback for use with useFocusEffect
  const fetchContactCompletion = useCallback(async () => {
    try {
      // Add timeout to prevent infinite loading
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Contact completion fetch timeout')), 5000);
      });

      // For contact tasks, determine completion based on actual contact data
      // Pass targetUserId if viewing another user's data
      const targetUserId = isViewingOthersData ? selectedUserId : undefined;
      const fetchPromise = contactService.getAllContacts(targetUserId);
      const allContacts = await Promise.race([fetchPromise, timeoutPromise]);

      const completedContactTasksList: string[] = [];

      CONTACTS_TASKS_CONFIG.forEach(task => {
        if (task.category) {
          const categoryContacts = allContacts.filter(c => c.category.toLowerCase() === task.category!.toLowerCase());
          if (categoryContacts.length > 0) {
            completedContactTasksList.push(task.id.toString());
          }
        }
      });

      setCompletedContactTasks(completedContactTasksList);
    } catch (error) {
      logger.error('Error fetching completed contact tasks (using fallback):', error);
      setCompletedContactTasks([]);
    }
  }, [isViewingOthersData, selectedUserId]);

  // Refresh contact completion when screen gains focus
  useFocusEffect(
    useCallback(() => {
      logger.debug('EmergencyContactsChecklistScreen focused - refreshing contact completion data');
      fetchContactCompletion();
    }, [fetchContactCompletion])
  );

  useEffect(() => {
    const fetchCompletedTasks = async () => {
      try {
        const token = await getAuthToken();
        if (!token) {
          setCompletedTaskIds([]);
          return;
        }
        const response = await fetch(`${API_URL}/completed-tasks`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) {
          setCompletedTaskIds([]);
          return;
        }
        const data = await response.json();
        setCompletedTaskIds(data.completedTasks || []);
      } catch (error) {
        logger.error('Error fetching completed tasks:', error);
        setCompletedTaskIds([]);
      }
    };

    const fetchCategoryProgress = async () => {
      try {
        const token = await getAuthToken();
        if (!token) return;

        const response = await fetch(`${API_URL}/category-progress`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (!response.ok) return;

        const data = await response.json();
        setCategoryProgress(data.progress || {});
      } catch (error) {
        logger.error('Error fetching category progress:', error);
      }
    };

    const fetchPasswordCompletion = async () => {
      try {
        // For password tasks, determine completion based on actual password data
        const allPasswords = await vaultService.getAllPasswords();
        const completedPasswordTasks: string[] = [];

        PASSWORDS_TASKS_CONFIG.forEach(task => {
          if (task.category) {
            const categoryPasswords = allPasswords.filter(p => p.category.toLowerCase() === task.category.toLowerCase());
            if (categoryPasswords.length > 0) {
              completedPasswordTasks.push(task.id.toString());
            }
          }
        });

        setCompletedPasswordTasks(completedPasswordTasks);
      } catch (error) {
        logger.error('Error fetching completed password tasks:', error);
        setCompletedPasswordTasks([]);
      }
    };

    const fetchExpenseCompletion = async () => {
      try {
        const allExpenses = await expenseService.getAllExpenses();
        const completedExpenseTasks: string[] = [];

        EXPENSES_TASKS_CONFIG.forEach(task => {
          if (task.category) {
            const categoryExpenses = allExpenses.filter(e => e.category.toLowerCase() === task.category.toLowerCase());
            if (categoryExpenses.length > 0) {
              completedExpenseTasks.push(task.id.toString());
            }
          }
        });

        setCompletedExpenseTasks(completedExpenseTasks);
      } catch (error) {
        logger.error('Error fetching completed expense tasks:', error);
        setCompletedExpenseTasks([]);
      }
    };

    const fetchLoyaltyPointCompletion = async () => {
      try {
        const allLoyaltyPoints = await loyaltyPointService.getAllLoyaltyPoints();
        const completedLoyaltyPointTasks: string[] = [];

        LOYALTY_POINTS_TASKS_CONFIG.forEach(task => {
          if (task.category) {
            const categoryLoyaltyPoints = allLoyaltyPoints.filter(lp => lp.category.toLowerCase() === task.category.toLowerCase());
            if (categoryLoyaltyPoints.length > 0) {
              completedLoyaltyPointTasks.push(task.id.toString());
            }
          }
        });

        setCompletedLoyaltyPointTasks(completedLoyaltyPointTasks);
      } catch (error) {
        logger.error('Error fetching completed loyalty point tasks:', error);
        setCompletedLoyaltyPointTasks([]);
      }
    };

    const fetchDocumentCompletion = async () => {
      try {
        const allDocuments = await documentStorageService.listDocuments();
        const completedDocumentTasksList: string[] = [];

        const DOCUMENT_CATEGORIES = ['personal', 'legal', 'medical', 'financial', 'property', 'insurance', 'digital', 'other'];
        const DOCUMENT_TASK_IDS = ['dm1', 'dm2', 'dm3', 'dm4', 'dm5', 'dm6', 'dm7', 'dm8'];

        DOCUMENT_CATEGORIES.forEach((category, index) => {
          const categoryDocuments = allDocuments.filter(doc => doc.category?.toLowerCase() === category);
          if (categoryDocuments.length > 0) {
            completedDocumentTasksList.push(DOCUMENT_TASK_IDS[index]);
          }
        });

        setCompletedDocumentTasks(completedDocumentTasksList);
      } catch (error) {
        logger.error('Error fetching completed document tasks:', error);
        setCompletedDocumentTasks([]);
      }
    };

    const fetchCompletedTasksFromBackend = async () => {
      try {
        const token = await getAuthToken();
        if (!token) return;

        const res = await fetch(`${API_URL}/api/tasks/user/completed-tasks`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (res.ok) {
          const data = await res.json();
          const completedTaskIds = (data.completedTaskIds || []).map((id: any) => Number(id));

          const personalIds = new Set(PERSONAL_TASKS_CONFIG.map(t => Number(t.id)));
          const medicalIds = new Set(MEDICAL_TASKS_CONFIG.map(t => Number(t.id)));
          const financeIds = new Set(FINANCE_TASKS_CONFIG.map(t => Number(t.id)));
          const propertyIds = new Set(PROPERTY_TASKS_CONFIG.map(t => Number(t.id)));
          const finalWishesIds = new Set(FINAL_WISHES_TASKS_CONFIG.map(t => Number(t.id)));
          const healthcareDirectivesIds = new Set(HEALTHCARE_DIRECTIVES_TASKS_CONFIG.map(t => Number(t.id)));
          const estatePlanningIds = new Set(ESTATE_PLANNING_TASKS_CONFIG.map(t => Number(t.id)));
          const carePreferencesIds = new Set(CARE_PREFERENCES_TASKS_CONFIG.map(t => Number(t.id)));

          setCompletedPersonalTasks(completedTaskIds.filter((id: number) => personalIds.has(id)).map((id: number) => id.toString()));
          setCompletedMedicalTasks(completedTaskIds.filter((id: number) => medicalIds.has(id)).map((id: number) => id.toString()));
          setCompletedFinanceTasks(completedTaskIds.filter((id: number) => financeIds.has(id)).map((id: number) => id.toString()));
          setCompletedPropertyTasks(completedTaskIds.filter((id: number) => propertyIds.has(id)).map((id: number) => id.toString()));
          setCompletedFinalWishesTasks(completedTaskIds.filter((id: number) => finalWishesIds.has(id)).map((id: number) => id.toString()));
          setCompletedHealthcareDirectivesTasks(completedTaskIds.filter((id: number) => healthcareDirectivesIds.has(id)).map((id: number) => id.toString()));
          setCompletedEstatePlanningTasks(completedTaskIds.filter((id: number) => estatePlanningIds.has(id)).map((id: number) => id.toString()));
          setCompletedCarePreferencesTasks(completedTaskIds.filter((id: number) => carePreferencesIds.has(id)).map((id: number) => id.toString()));
        }
      } catch (error) {
        logger.error('Error fetching completed tasks from backend:', error);
      }
    };

    fetchCompletedTasks();
    fetchCategoryProgress();
    // fetchContactCompletion is now handled by useFocusEffect
    fetchPasswordCompletion();
    fetchExpenseCompletion();
    fetchLoyaltyPointCompletion();
    fetchDocumentCompletion();
    fetchCompletedTasksFromBackend();
  }, [isViewingOthersData, selectedUserId]);

  const handleCategoryPress = (categoryId: string) => {
    setSelectedCategory(categoryId);
    switch (categoryId) {
      case 'all':
        navigation.navigate('Dashboard', { screen: 'AllTasks' });
        break;
      case 'personal':
        navigation.navigate('Dashboard', { screen: 'PersonalChecklist' });
        break;
      case 'medical':
        navigation.navigate('Dashboard', { screen: 'MedicalChecklist' });
        break;
      case 'finance':
        navigation.navigate('Dashboard', { screen: 'FinanceChecklist' });
        break;
      case 'property':
        navigation.navigate('Dashboard', { screen: 'PropertyChecklist' });
        break;
      case 'finalWishes':
        navigation.navigate('Dashboard', { screen: 'FinalWishesChecklist' });
        break;
      case 'healthcareDirectives':
        navigation.navigate('Dashboard', { screen: 'HealthcareDirectivesChecklist' });
        break;
      case 'estatePlanning':
        navigation.navigate('Dashboard', { screen: 'EstatePlanningChecklist' });
        break;
      case 'carePreferences':
        navigation.navigate('Dashboard', { screen: 'CarePreferencesChecklist' });
        break;
      case 'passwords':
        navigation.navigate('Dashboard', { screen: 'OnlineAccountsChecklist' });
        break;
      case 'contacts':
        navigation.navigate('Dashboard', { screen: 'EmergencyContactsChecklist' });
        break;
      case 'expenses':
        navigation.navigate('Dashboard', { screen: 'ExpensesChecklist' });
        break;
      case 'documents':
        navigation.navigate('Dashboard', { screen: 'DocumentManagementChecklist' });
        break;
      case 'loyaltyPoints':
        navigation.navigate('Dashboard', { screen: 'LoyaltyPointsChecklist' });
        break;
    }
  };

  const handleTaskPress = async (taskId: string) => {
    logger.debug('handleTaskPress called with taskId:', taskId);
    logger.debug('CONTACTS_TASKS_CONFIG:', CONTACTS_TASKS_CONFIG);

    // Find the task to get its category
    const task = CONTACTS_TASKS_CONFIG.find(t => t.id.toString() === taskId);
    logger.debug('Found task:', task);

    if (task && task.category) {
      logger.debug('Navigating to ContactsDashboardScreen with category:', task.category);
      // Navigate to ContactsDashboardScreen with the selected category
      // Pass targetUserId and viewOnly if viewing another user's data
      navigation.navigate('Contacts' as never, {
        screen: 'ContactsDashboardScreen',
        params: {
          selectedCategory: task.category,
          targetUserId: isViewingOthersData ? selectedUserId : undefined,
          viewOnly: isViewingOthersData
        }
      } as never);
      return;
    }

    try {
      const token = await getAuthToken();
      if (!token) return;

      const isCompleted = completedTaskIds.includes(taskId);
      const method = isCompleted ? 'DELETE' : 'POST';
      const url = `${API_URL}/completed-tasks/${taskId}`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) return;

      setCompletedTaskIds(prev => {
        if (isCompleted) {
          return prev.filter(id => id !== taskId);
        } else {
          return [...prev, taskId];
        }
      });

      // Refresh category progress after task completion
      const progressResponse = await fetch(`${API_URL}/category-progress`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (progressResponse.ok) {
        const data = await progressResponse.json();
        setCategoryProgress(data.progress || {});
      }
    } catch (error) {
      logger.error('Error updating task completion:', error);
    }
  };

  const getCategoryCount = (categoryId: string) => {
    if (categoryId === 'all') {
      // For all category, calculate total completion across all categories
      return completedPersonalTasks.length + completedMedicalTasks.length + completedFinanceTasks.length +
             completedPropertyTasks.length + completedFinalWishesTasks.length + completedHealthcareDirectivesTasks.length +
             completedEstatePlanningTasks.length + completedCarePreferencesTasks.length +
             completedPasswordTasks.length + completedContactTasks.length + completedExpenseTasks.length +
             completedLoyaltyPointTasks.length + completedDocumentTasks.length;
    } else if (categoryId === 'personal') {
      // For personal category, use personal data to calculate progress
      return completedPersonalTasks.length;
    } else if (categoryId === 'medical') {
      // For medical category, use medical data to calculate progress
      return completedMedicalTasks.length;
    } else if (categoryId === 'finance') {
      // For finance category, use finance data to calculate progress
      return completedFinanceTasks.length;
    } else if (categoryId === 'property') {
      // For property category, use property data to calculate progress
      return completedPropertyTasks.length;
    } else if (categoryId === 'finalWishes') {
      // For final wishes category, use final wishes data to calculate progress
      return completedFinalWishesTasks.length;
    } else if (categoryId === 'healthcareDirectives') {
      // For healthcare directives category, use healthcare directives data to calculate progress
      return completedHealthcareDirectivesTasks.length;
    } else if (categoryId === 'estatePlanning') {
      // For estate planning category, use estate planning data to calculate progress
      return completedEstatePlanningTasks.length;
    } else if (categoryId === 'carePreferences') {
      // For care preferences category, use care preferences data to calculate progress
      return completedCarePreferencesTasks.length;
    } else if (categoryId === 'contacts') {
      // For contacts category, use contact data to calculate progress
      return completedContactTasks.length;
    } else if (categoryId === 'passwords') {
      // For passwords category, use password data to calculate progress
      return completedPasswordTasks.length;
    } else if (categoryId === 'expenses') {
      // For expenses category, use expense data to calculate progress
      return completedExpenseTasks.length;
    } else if (categoryId === 'documents') {
      // For documents category, use document data to calculate progress
      return completedDocumentTasks.length;
    } else if (categoryId === 'loyaltyPoints') {
      // For loyalty points category, use loyalty point data to calculate progress
      return completedLoyaltyPointTasks.length;
    } else {
      // For other categories, return 0
      return 0;
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.mainContent}>
        <View style={styles.header}>
          <TouchableOpacity 
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <View style={styles.headerPlaceholder} />
        </View>
        <ChecklistCardScroll
          categories={categories.map(cat => ({
            ...cat,
            count: getCategoryCount(cat.id),
            totalTasks: cat.totalTasks
          }))}
          selectedCategory={selectedCategory}
          onCategoryPress={handleCategoryPress}
          navigation={navigation}
        />
        <Text style={styles.title}>Emergency Contacts Checklist</Text>

        {/* Locked state when viewing another user's data - contacts are EOL only */}
        {isViewingOthersData ? (
          <View style={styles.lockedContainer}>
            <View style={styles.lockedIconContainer}>
              <Text style={styles.lockedIcon}>🔒</Text>
            </View>
            <Text style={styles.lockedTitle}>Contacts Locked</Text>
            <Text style={styles.lockedDescription}>
              {viewingUserName}'s contacts are only accessible after an End of Life event has been verified.
            </Text>
            <TouchableOpacity
              style={styles.backToDashboardButton}
              onPress={() => navigation.navigate('Dashboard' as never)}
            >
              <Text style={styles.backToDashboardText}>Back to Dashboard</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <>
            {!isViewingOthersData && <UpgradeBanner variant="compact" />}

            {/* View All Contacts Button */}
            {completedContactTasks.length > 0 && (
              <TouchableOpacity
                style={styles.viewAllButton}
                onPress={() => navigation.navigate('Contacts' as never, {
                  screen: 'ContactsDashboardScreen',
                  params: {
                    targetUserId: isViewingOthersData ? selectedUserId : undefined,
                    viewOnly: isViewingOthersData
                  }
                } as never)}
              >
                <Text style={styles.viewAllButtonText}>View All Contacts</Text>
              </TouchableOpacity>
            )}

            <ScrollView style={styles.contentContainer} contentContainerStyle={{ paddingBottom: 200 }}>
              {EMERGENCY_CONTACTS_TASKS.map(task => {
                const isCompleted = completedContactTasks.includes(task.id.toString());
                const buttonText = isCompleted ? 'Manage Contacts' : 'Get Started';
                const buttonColor = isCompleted ? '#FF9F5A' : '#29BFC0';
                const isTaskDisabled = !canViewOwnData && !isViewingOthersData;

                // Convert task to match TaskItem interface (id as string)
                const taskForItem = {
                  id: task.id.toString(),
                  title: task.title
                };

                return (
                  <TaskItem
                    key={task.id}
                    task={taskForItem}
                    onPress={handleTaskPress}
                    isCompleted={isCompleted}
                    onCheckboxPress={() => {}}
                    buttonText={buttonText}
                    buttonColor={buttonColor}
                    isDisabled={isTaskDisabled}
                  />
                );
              })}
            </ScrollView>
          </>
        )}
      </View>
      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    paddingTop: ms(25),
  },
  mainContent: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: '10%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(30),
    height: s(30),
    tintColor: '#333',
  },
  logo: {
    width: ms(125, 0.3),
    height: ms(125, 0.3),
  },
  headerPlaceholder: {
    width: s(30),
    height: s(30),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginLeft: ms(30),
    marginVertical: 0,
    marginBottom: ms(18),
  },
  contentContainer: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  viewAllButton: {
    backgroundColor: '#29BFC0',
    paddingVertical: ms(12),
    paddingHorizontal: ms(24),
    borderRadius: ms(8),
    marginHorizontal: ms(15),
    marginBottom: ms(20),
    alignItems: 'center',
  },
  viewAllButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(10),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(15),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
  lockedContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: ms(40),
    paddingBottom: ms(100),
  },
  lockedIconContainer: {
    width: s(100),
    height: s(100),
    borderRadius: ms(50),
    backgroundColor: '#FF9F5A',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: ms(24),
  },
  lockedIcon: {
    fontSize: ms(48, 0.3),
  },
  lockedTitle: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(16),
    textAlign: 'center',
  },
  lockedDescription: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: ms(24, 0.3),
    marginBottom: ms(32),
  },
  backToDashboardButton: {
    backgroundColor: '#29BFC0',
    paddingVertical: ms(14),
    paddingHorizontal: ms(32),
    borderRadius: ms(25),
  },
  backToDashboardText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
}); 